import {
  Box,
  Heading,
  Stack,
  List,
  ListItem,
  ListIcon,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useContext } from "react";
import CurrencyContext from "../../context/CurrencyContext";
import { Trans } from "@lingui/macro";
import { AccomodationSummary as AccomodationSummaryType } from "../../types/accomodation.types";
import { formatDateForInput } from "../../utils/date-formatter";
import { FaHotel, FaBed, FaRegCalendarAlt, FaMoon } from "react-icons/fa";
import { formatPrice } from "../../utils/price-formatter";

interface AccomodationSummaryProps {
  accomodationSummary: AccomodationSummaryType | null;
  originalSummary?: AccomodationSummaryType | null;
}

export const AccomodationSummary: React.FC<AccomodationSummaryProps> = ({
  accomodationSummary,
  originalSummary,
}) => {
  const currency = useContext(CurrencyContext);
  const hasChanges = originalSummary !== undefined;

  // Helper to check if dates/nights changed
  const datesChanged =
    hasChanges &&
    originalSummary &&
    accomodationSummary &&
    (formatDateForInput(originalSummary.arrivalDate) !==
      formatDateForInput(accomodationSummary.arrivalDate) ||
      formatDateForInput(originalSummary.departureDate) !==
        formatDateForInput(accomodationSummary.departureDate) ||
      originalSummary.numberOfNights !== accomodationSummary.numberOfNights);

  // Helper to check if roommate name changed
  const roommateChanged =
    hasChanges &&
    (originalSummary?.roomMateName || "-") !==
      (accomodationSummary?.roomMateName || "-");

  // Helper to check if price changed
  const priceChanged =
    hasChanges &&
    originalSummary &&
    accomodationSummary &&
    originalSummary.price !== accomodationSummary.price;

  // Check if originally was "not ordered" (null or 0 nights)
  const originallyNotOrdered =
    hasChanges && (!originalSummary || originalSummary.numberOfNights === 0);

  const hasAnyChanges =
    datesChanged || roommateChanged || priceChanged || originallyNotOrdered;

  return (
    <Box
      borderRadius={hasAnyChanges ? "lg" : "none"}
      _before={
        hasAnyChanges
          ? {
              content: '""',
              position: "absolute",
              inset: "0px",
              zIndex: -1,
              borderRadius: "lg",
              filter: "blur(4px)",
              pointerEvents: "none",
              boxShadow: `
                -3px -3px 8px rgba(245, 101, 101, 0.15),
                1.5px -2px 9px rgba(237, 100, 166, 0.15),
                -4.5px  1.5px 10px rgba(237, 137,  54, 0.15),
                1.5px  3px 8px rgba(237, 100, 166, 0.125),
                -3px 5.5px 8px rgba(245, 101, 101, 0.125),
                3px -3px 9px rgba(237, 137,  54, 0.125)
              `,
            }
          : undefined
      }
      zIndex={0}
      position="relative"
    >
      <Stack
        px={0}
        py={3}
        justifyContent={{
          base: "flex-start",
          md: "space-between",
        }}
        direction={{
          base: "column",
          md: "row",
        }}
        alignItems={{ md: "center" }}
      >
        {accomodationSummary && (
          <>
            <Heading size={"md"} w={{ md: "34%" }}>
              {accomodationSummary.hotelName}
            </Heading>
            <List spacing={3} textAlign="start" w={{ md: "40%" }}>
              <ListItem alignItems={"baseline"}>
                {" "}
                <HStack gap={0}>
                  <ListIcon as={FaHotel} color="green.500" />
                  <Text>{accomodationSummary.hotelRoomName}</Text>
                </HStack>
              </ListItem>
              <ListItem alignItems={"baseline"}>
                <HStack gap={0} alignItems={priceChanged ? "baseline" : "center"}>
                  <ListIcon as={FaMoon} color="green.500" />
                    {priceChanged ? (
                      <VStack>
                        <Text as="span" color="green.500" fontWeight={600}>
                          {formatPrice(accomodationSummary.price)} {currency} /{" "}
                          <Trans>night</Trans>
                        </Text>
                        {originalSummary && (
                          <Text
                            as="span"
                            color="red.500"
                            ml={2}
                            textDecoration="line-through"
                          >
                            ({formatPrice(originalSummary.price)} {currency} /{" "}
                            <Trans>night</Trans>)
                          </Text>
                        )}
                      </VStack>
                    ) : (
                      <Text whiteSpace={"nowrap"}>
                        {formatPrice(accomodationSummary.price)} {currency} /{" "}
                        <Trans>night</Trans>
                        </Text>
                    )}
                </HStack>
              </ListItem>
              <ListItem alignItems={"baseline"}>
                <HStack gap={0} alignItems={datesChanged || originallyNotOrdered ? "baseline" : "center"}>
                  <ListIcon as={FaRegCalendarAlt} color="green.500" />
                    {datesChanged || originallyNotOrdered ? (
                      <VStack>
                        <Text as="span" color="green.500" fontWeight={600} whiteSpace={"nowrap"}>
                          {formatDateForInput(accomodationSummary.arrivalDate)}{" "}
                          -{" "}
                          {formatDateForInput(
                            accomodationSummary.departureDate
                          )}{" "}
                          ({accomodationSummary.numberOfNights}{" "}
                          <Trans>nights</Trans>)
                        </Text>
                        {originallyNotOrdered ? (
                          <Text
                            as="span"
                            color="red.500"
                            ml={2}
                            textDecoration="line-through"
                          >
                            (<Trans>Not ordered</Trans>)
                          </Text>
                        ) : originalSummary ? (
                          <Text
                            as="span"
                            color="red.500"
                            ml={2}
                            textDecoration="line-through"
                            whiteSpace={"nowrap"}
                          >
                            ({formatDateForInput(originalSummary.arrivalDate)} -{" "}
                            {formatDateForInput(originalSummary.departureDate)}{" "}
                            ({originalSummary.numberOfNights}{" "}
                            <Trans>nights</Trans>) )
                          </Text>
                        ) : null}
                      </VStack>
                    ) : (
                      <Text whiteSpace={"nowrap"}>
                        {formatDateForInput(accomodationSummary.arrivalDate)} -{" "}
                        {formatDateForInput(accomodationSummary.departureDate)}{" "}
                        ({accomodationSummary.numberOfNights}{" "}
                        <Trans>nights</Trans>)
                      </Text>
                    )}
                  
                </HStack>
              </ListItem>
              <ListItem alignItems={"baseline"}>
                <HStack gap={0}>
                  <ListIcon as={FaBed} color="green.500" />
                  <Text>
                    <Trans>Name of roommate(s)</Trans>:{" "}
                    {roommateChanged ? (
                      <>
                        <Text as="span" color="green.500" fontWeight={600}>
                          {accomodationSummary.roomMateName || "-"}
                        </Text>
                        <Text
                          as="span"
                          color="red.500"
                          ml={2}
                          textDecoration="line-through"
                        >
                          ({originalSummary?.roomMateName || "-"})
                        </Text>
                      </>
                    ) : (
                      accomodationSummary.roomMateName || "-"
                    )}
                  </Text>
                </HStack>
              </ListItem>
            </List>
            <Heading size={"xl"} w={{ md: "24%" }} textAlign={{ md: "end" }}>
              {priceChanged || datesChanged || originallyNotOrdered ? (
                <>
                  <Text as="span" color="green.500" fontWeight={600} whiteSpace={"nowrap"}>
                    {formatPrice(
                      accomodationSummary.price *
                        accomodationSummary.numberOfNights
                    )}{" "}
                    {currency}
                  </Text>
                  {originalSummary && (
                    <Text
                      as="span"
                      color="red.500"
                      ml={2}
                      textDecoration="line-through"
                      fontSize="lg"
                    >
                      (
                      {formatPrice(
                        originalSummary.price * originalSummary.numberOfNights
                      )}{" "}
                      {currency})
                    </Text>
                  )}
                  {originallyNotOrdered && !originalSummary && (
                    <Text
                      as="span"
                      color="red.500"
                      ml={2}
                      textDecoration="line-through"
                      fontSize="lg"
                    >
                      (0 {currency})
                    </Text>
                  )}
                </>
              ) : (
                <>
                  {formatPrice(
                    accomodationSummary.price *
                      accomodationSummary.numberOfNights
                  )}{" "}
                  {currency}
                </>
              )}
            </Heading>
          </>
        )}
        {!accomodationSummary && (
          <>
            <Text w={{ md: "39%" }}>
              <Trans>Not ordered</Trans>
            </Text>
            <Stack w={{ md: "40%" }} />
            <Heading size={"xl"} w={{ md: "19%" }} textAlign={{ md: "end" }}>
              0 {currency}
            </Heading>
          </>
        )}
      </Stack>
    </Box>
  );
};
